import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'icon'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-accent-foreground hover:opacity-90 border-transparent',
  secondary: 'bg-surface text-foreground hover:bg-muted border-border',
  ghost: 'bg-transparent text-foreground hover:bg-muted border-transparent',
  danger: 'bg-danger text-danger-foreground hover:opacity-90 border-transparent',
}

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  icon: 'h-9 w-9 justify-center',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', loading = false, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center rounded-md border font-medium transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  )
})

/**
 * Wraps a disabled control so a native tooltip still appears.
 *
 * Browsers do not fire hover events on disabled buttons, so a `title` placed
 * directly on one is never shown. This matters here because the delete button
 * is routinely disabled and the reason — a retention period or hold — is the
 * single most useful thing to tell the user at that moment.
 */
export function ExplainDisabled({
  reason,
  children,
}: {
  reason: string | null
  children: React.ReactNode
}) {
  if (!reason) return <>{children}</>
  return (
    <span title={reason} className="inline-flex">
      {children}
    </span>
  )
}
